'use client';

import React from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { useTheme } from 'styled-components';

export interface SparklineProps {
  /** Data points in temporal order. Any numeric scale. */
  data: number[];
  /** Stroke + fill colour. Defaults to `theme.colors.brand.primary`. */
  color?: string;
  /** Fill the area under the line. Default false. */
  filled?: boolean;
  /** Chart height in px. Default 32. */
  height?: number;
  'aria-label'?: string;
  className?: string;
}

/**
 * Compact recharts trend chart. Designed for use inside `KPICell` or inline
 * with summary text. Width fills its container; height is fixed.
 *
 * @example
 * <Sparkline data={[10, 20, 15, 30]} filled />
 */
export function Sparkline({
  data,
  color,
  filled = false,
  height = 32,
  'aria-label': ariaLabel,
  className,
}: SparklineProps) {
  const theme = useTheme() as any;
  const strokeColor = color ?? theme.colors.brand.primary;

  const direction =
    data.length >= 2
      ? data[data.length - 1] > data[0]
        ? 'up'
        : data[data.length - 1] < data[0]
          ? 'down'
          : 'flat'
      : 'flat';

  const label =
    ariaLabel ??
    (data.length >= 2
      ? `Trend: ${data[0]} to ${data[data.length - 1]}, ${direction}`
      : 'Trend data');

  const chartData = data.map((v) => ({ v }));

  return (
    <div role="img" aria-label={label} className={className} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Area
            type="monotone"
            dataKey="v"
            stroke={strokeColor}
            strokeWidth={1.5}
            fill={strokeColor}
            fillOpacity={filled ? 0.15 : 0}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
